/**
 * VÉRAULT Portal — Advisor: Payments
 *
 * Replatformed (not ported). AR/AP rollup with quiet operational AI:
 *  - Anomalies card surfaces overdue / due-soon / large-outstanding / stale-pending
 *    counts at the top (rule-based, deterministic, fast).
 *  - At-a-glance metric tiles for AR / AP / Outstanding / Overdue.
 *  - Group-by switcher (Client / Trip / Status / Direction) drives the table.
 */
import React from "react";
import { motion } from "framer-motion";
import { CreditCard, AlertCircle, Clock, Coins, Hourglass } from "lucide-react";
import {
  Surface,
  Heading,
  PageHeader,
  EditorialRule,
  MetricTile,
  StatusTag,
  Empty,
  Skeleton,
  PortalButton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

const GROUPS = [
  { id: "client", label: "By client" },
  { id: "trip", label: "By trip" },
  { id: "status", label: "By status" },
  { id: "direction", label: "AR vs AP" },
];

// --- Anomalies row -------------------------------------------------
function AnomaliesRow({ data }) {
  if (!data) return null;
  const items = [
    { id: "overdue", icon: AlertCircle, label: "Overdue", count: data.overdue?.length || 0, tone: "warn" },
    { id: "due_soon", icon: Clock, label: "Due soon", count: data.due_soon?.length || 0, tone: "accent" },
    { id: "large", icon: Coins, label: "Large outstanding", count: data.large_outstanding?.length || 0, tone: "default" },
    { id: "stale", icon: Hourglass, label: "Stale pending", count: data.stale_pending?.length || 0, tone: "quiet" },
  ];
  const allZero = items.every((x) => x.count === 0);
  if (allZero) {
    return (
      <Surface variant="paper">
        <p className="font-accent italic text-[15px] text-[hsl(var(--foreground-secondary))]">
          No anomalies. The books look calm.
        </p>
      </Surface>
    );
  }
  return (
    <Surface variant="paper">
      <Heading level="eyebrow">Worth a look</Heading>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-6">
        {items.map(({ id, icon: Icon, label, count, tone }) => (
          <div key={id} data-testid={`anomaly-${id}`} className="flex flex-col gap-2">
            <Icon className={`size-4 ${tone === "warn" ? "text-[hsl(var(--accent-terracotta))]" : tone === "accent" ? "text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]"}`} strokeWidth={1.5} />
            <p className={`font-serif text-2xl leading-none ${count === 0 ? "text-[hsl(var(--foreground-muted))]" : "text-[hsl(var(--foreground))]"}`}>
              {count}
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
              {label}
            </p>
          </div>
        ))}
      </div>
    </Surface>
  );
}

// --- Page ---------------------------------------------------------
export default function AdvisorPayments() {
  const [summary, setSummary] = React.useState(null);
  const [rollup, setRollup] = React.useState(null);
  const [anomalies, setAnomalies] = React.useState(null);
  const [group, setGroup] = React.useState("client");
  const [error, setError] = React.useState(null);

  // 1. Top-level summary + anomalies on mount
  React.useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/payments").then((r) => r.data),
      api.get("/payments/anomalies").then((r) => r.data).catch(() => null),
    ])
      .then(([pay, anom]) => {
        if (!alive) return;
        setSummary(pay?.summary || null);
        setAnomalies(anom);
      })
      .catch(() => alive && setError("Couldn't load payments."));
    return () => {
      alive = false;
    };
  }, []);

  // 2. Rollup re-fetches whenever the group switches
  React.useEffect(() => {
    let alive = true;
    setRollup(null);
    api
      .get("/payments/rollup", { params: { group_by: group } })
      .then(({ data }) => alive && setRollup(data))
      .catch(() => alive && setRollup({ rows: [] }));
    return () => {
      alive = false;
    };
  }, [group]);

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Payments"
          title="The books"
          lede="Receivables, payables, and what wants attention today."
        />
      </motion.div>

      {error && (
        <Empty title={error} icon={<CreditCard className="size-6" strokeWidth={1.25} />} />
      )}

      <motion.div variants={fadeInUp}>
        <AnomaliesRow data={anomalies} />
      </motion.div>

      <motion.div variants={fadeInUp}>
        <EditorialRule label="At a glance" />
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {summary ? (
            <>
              <MetricTile label="Outstanding · AR" value={summary.total_ar_outstanding} tone={summary.total_ar_outstanding > 0 ? "warn" : "default"} caption="Clients still owe us" testid="metric-ar-out" />
              <MetricTile label="Outstanding · AP" value={summary.total_ap_outstanding} tone="quiet" caption="We still owe vendors" testid="metric-ap-out" />
              <MetricTile label="Overdue" value={summary.overdue_amount} tone={summary.overdue_amount > 0 ? "warn" : "default"} caption={`${summary.total_overdue} item${summary.total_overdue === 1 ? "" : "s"}`} testid="metric-overdue" />
              <MetricTile label="Total collected" value={summary.total_paid} tone="accent" caption="Lifetime" testid="metric-collected" />
            </>
          ) : (
            [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)
          )}
        </div>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <EditorialRule label="Rollup" className="flex-1" />
          <nav className="flex gap-1" data-testid="payments-group">
            {GROUPS.map((g) => (
              <button
                key={g.id}
                onClick={() => setGroup(g.id)}
                data-testid={`payments-group-${g.id}`}
                className={`group relative px-3 py-1.5 font-body text-[13px] tracking-tight transition-colors duration-200 ${
                  group === g.id ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                }`}
              >
                <span className={group === g.id ? "font-serif" : ""}>{g.label}</span>
                <span
                  className={`absolute -bottom-px left-3 right-3 h-px ${group === g.id ? "bg-[hsl(var(--accent-gold))]" : "bg-transparent"}`}
                  aria-hidden
                />
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-5">
          {!rollup ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : !rollup.rows || rollup.rows.length === 0 ? (
            <Empty title="No payments yet" icon={<CreditCard className="size-6" strokeWidth={1.25} />} />
          ) : (
            <Surface variant="default" padded={false} className="overflow-hidden">
              <div className="grid grid-cols-[1fr_repeat(4,90px)] sm:grid-cols-[1fr_repeat(4,110px)] gap-3 sm:gap-4 px-5 sm:px-6 py-3 text-right font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] border-b border-[rgba(0,0,0,0.05)]">
                <span className="text-left">{GROUPS.find((g) => g.id === group)?.label || group}</span>
                <span>Paid</span>
                <span>Pending</span>
                <span>Overdue</span>
                <span>Total</span>
              </div>
              <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                {rollup.rows.map((r) => (
                  <li key={r.key}>
                    <div className="grid grid-cols-[1fr_repeat(4,90px)] sm:grid-cols-[1fr_repeat(4,110px)] gap-3 sm:gap-4 px-5 sm:px-6 py-4 items-center text-right font-mono text-[12px] text-[hsl(var(--foreground))]" data-testid={`rollup-${r.key}`}>
                      <span className="text-left font-body text-[14px] truncate">{r.label}</span>
                      <span className="text-[hsl(var(--accent-sage))]">{fmt.money(r.paid)}</span>
                      <span className="text-[hsl(var(--foreground-secondary))]">{fmt.money(r.pending)}</span>
                      <span className={r.overdue > 0 ? "text-[hsl(var(--accent-terracotta))]" : "text-[hsl(var(--foreground-muted))]"}>
                        {fmt.money(r.overdue)}
                      </span>
                      <span className="font-serif text-[14px]">{fmt.money(r.total)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </Surface>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
